import { paywallFunnelStep } from "utils/paywall-funnel"

// Events that start a Sentry Replay recording.
const REPLAY_TRIGGER_EVENTS = ["paywall_view"]

// Rendered by layouts/web/_sentry_js.html.slim from the
// sentry_js_replays_paywall_rate feature flag; absent = never trigger.
const RATE_META_NAME = "sentry-replay-paywall-rate"

// Ecommerce fields copied into the log attributes; items stay out.
const ECOMMERCE_FIELDS = ["transaction_id", "currency", "value", "coupon"]
const SCALAR_TYPES = ["string", "number", "boolean"]

let decided = false
let furthestTaggedStep = -1
const pendingEvents = []
let onLoadRegistered = false

function paywallRate() {
  const meta = document.querySelector(`meta[name="${RATE_META_NAME}"]`)
  const rate = meta ? parseFloat(meta.content) : NaN
  return Number.isFinite(rate) ? rate : 0
}

function sdkReady(sentry) {
  return (
    sentry && (typeof sentry.getClient === "function" || typeof sentry.getReplay === "function")
  )
}

// After the replay converts to a session recording, the scope still holds
// the DSC snapshot taken when the pageload span ended - before the replay
// id existed - and a stored DSC is never regenerated (and, lacking
// trace_id/public_key, is not even a valid DSC for Relay). Drop it so the
// next outgoing request rebuilds the baggage with sentry-replay_id, which
// is what links backend errors to this replay.
function refreshTracePropagation(sentry) {
  const scope = typeof sentry.getCurrentScope === "function" && sentry.getCurrentScope()
  if (!scope || typeof scope.getPropagationContext !== "function") return

  const context = scope.getPropagationContext()
  if (context && context.dsc) scope.setPropagationContext({ ...context, dsc: undefined })
}

// Scalar parameters only, plus a few ecommerce fields: nested data such as
// ecommerce items never reaches the logs.
function eventAttributes(params, funnelStep) {
  const attributes = {}
  if (funnelStep >= 0) attributes.funnel_step = funnelStep
  Object.keys(params).forEach((key) => {
    if (SCALAR_TYPES.includes(typeof params[key])) attributes[key] = params[key]
  })
  const { ecommerce } = params
  if (ecommerce && typeof ecommerce === "object") {
    ECOMMERCE_FIELDS.forEach((field) => {
      if (SCALAR_TYPES.includes(typeof ecommerce[field])) attributes[field] = ecommerce[field]
    })
  }
  return attributes
}

function recordWithLoadedSdk(sentry, event, params) {
  const funnelStep = paywallFunnelStep(event)
  const attributes = eventAttributes(params, funnelStep)

  if (typeof sentry.addBreadcrumb === "function") {
    sentry.addBreadcrumb({ category: "analytics", message: event, level: "info", data: attributes })
  }

  // Structured log per event: the Logs explorer can chart counts grouped by
  // the event attribute across all sessions, recorded or not.
  if (sentry.logger && typeof sentry.logger.info === "function") {
    sentry.logger.info("analytics", { event, ...attributes })
  }

  if (typeof sentry.setTag === "function") {
    if (funnelStep > furthestTaggedStep) {
      furthestTaggedStep = funnelStep
      sentry.setTag("paywall", event)
    }
    if (event === "paywall_view" && typeof params.touchpoint === "string") {
      sentry.setTag("touchpoint", params.touchpoint)
    }
  }

  if (decided || !REPLAY_TRIGGER_EVENTS.includes(event)) return

  const replay = typeof sentry.getReplay === "function" ? sentry.getReplay() : null
  if (!replay) return

  decided = true
  if (Math.random() >= paywallRate()) return

  Promise.resolve(replay.flush())
    .then(() => refreshTracePropagation(sentry))
    .catch(() => {})
}

function drainPendingEvents() {
  const sentry = window.Sentry
  if (!sdkReady(sentry)) return

  pendingEvents.splice(0).forEach(([event, params]) => {
    recordWithLoadedSdk(sentry, event, params)
  })
}

// Analytics adapter for Sentry: every event becomes a breadcrumb (visible on
// the replay timeline) and a structured log row; paywall funnel steps keep
// the session's paywall tag at the furthest step reached (so the Replays
// list can be segmented per step), and a paywall opening flushes the replay.
// Replay.flush() uploads the rolling buffer (up to ~60s of pre-paywall
// context) and keeps recording for the rest of the session, also for
// sessions that were not sampled into recording; on an already recording
// session it is a plain flush of the same replay. One sampling decision per
// page.
export default function sentryAnalyticsAdapter(event, params = {}) {
  const sentry = window.Sentry

  if (sdkReady(sentry)) {
    recordWithLoadedSdk(sentry, event, params)
    return
  }

  if (!sentry || typeof sentry.onLoad !== "function") return

  pendingEvents.push([event, params])
  if (onLoadRegistered) return

  onLoadRegistered = true
  sentry.onLoad(drainPendingEvents)
}
